import React, { createContext, useContext, useLayoutEffect, useMemo, useRef } from 'react'
import { createCommandRegistry, type CommandContext, type CommandDefinition, type CommandRegistry } from '../services/commandRegistry'
import { createRecentCommandPreferenceStore, recentCommandPreferenceKey, type CommandHistoryScope } from '../services/interfacePreferences'

interface CommandScope { registry: CommandRegistry; context: CommandContext | null; history: ReturnType<typeof createRecentCommandPreferenceStore> }
const Commands = createContext<CommandScope | null>(null)
export const useOptionalCommands = () => useContext(Commands)

/** Does not key/remount domain children. Only command state is replaced on scope change. */
export function CommandProvider({ scopeKey, context, historyScope = null, children }: React.PropsWithChildren<{
  scopeKey: string | null; context: CommandContext | null; historyScope?: CommandHistoryScope | null
}>) {
  const historyKey = recentCommandPreferenceKey(context && scopeKey ? historyScope : null)
  const history = useMemo(() => createRecentCommandPreferenceStore(historyKey), [historyKey])
  const token = useMemo(() => ({}), [scopeKey, historyKey])
  const live = useRef({ token, context, mounted: true })
  live.current = { token, context, mounted: true }
  const registry = useMemo(() => createCommandRegistry(() =>
    live.current.mounted && live.current.token === token && scopeKey !== null ? live.current.context : null,
    command => history.record(command.id)), [scopeKey, token, history])
  useLayoutEffect(() => {
    live.current.mounted = true
    return () => { live.current.mounted = false }
  }, [])
  const value = useMemo(() => ({ registry, context, history }), [registry, context, history])
  return <Commands.Provider value={value}>{children}</Commands.Provider>
}

export function useCommands() {
  const value = useContext(Commands)
  if (!value) throw new Error('Commands require CommandProvider')
  return value
}

/** Memoize definitions in the caller; effect cleanup retires their exact registrations. */
export function useWorkspaceCommands(commands: readonly CommandDefinition[]) {
  const { registry, context } = useCommands()
  const ready = context !== null
  useLayoutEffect(() => ready ? registry.register(commands) : undefined, [registry, ready, commands])
}
